// Lib
import { FC, useEffect, useState } from "react";
// Api
import {
  useLazyGetSettingsQuery,
  useUpdateSettingsMutation,
} from "redux/query/settingsAPI";
import {
  useLazyGetBalanceExpirationQuery,
  useUpdateBalanceExpirationMutation,
} from "redux/query/balanceExpirationAPI";
import {
  useLazyGetExchangeRateQuery,
  useUpdateExchangeRateMutation,
} from "redux/query/exchangeRateAPI";
import {
  useLazyGetAuthSettingsQuery,
  useUpdateAuthSettingsMutation,
} from "redux/query/authSettingsAPI";
import {
  useLazyGetPermissionLimitsQuery,
  useUpdatePermissionLimitsMutation,
} from "redux/query/permissionsAPI";
// Hooks
import { useNotification, usePermissions } from "hooks";
// Utils
import { errorHandler } from "utils/errorHandler";
// Components
import { Input, Switch, SettingsItem } from "./components";
// Styled
import {
  ContentBox,
  FlexContainer,
  PageHeadingContainer,
  PageTitleContainer,
} from "styled/Box";
import { Typography } from "styled/Typography";

import {
  StateValues,
  initialState,
  initialLoadingState,
  LoadingStateValues,
  notificationMessage,
  Field,
} from "./config";
import { Permission } from "consts";

export const Settings: FC = () => {
  const { openNotification } = useNotification();

  const {
    canExchangeRateUpdate,
    canSettingsUpdate,
    canBalanceExpirationUpdate,
    canSettingsGet,
  } = usePermissions();

  const [fetchSettings] = useLazyGetSettingsQuery();
  const [fetchBalanceExpiration] = useLazyGetBalanceExpirationQuery();
  const [fetchExchangeRate] = useLazyGetExchangeRateQuery();
  const [fetchAuthSettings] = useLazyGetAuthSettingsQuery();
  const [fetchPermissionsLimits] = useLazyGetPermissionLimitsQuery();

  const [updateAuthSettings] = useUpdateAuthSettingsMutation();
  const [updateSettings] = useUpdateSettingsMutation();
  const [updateExchangeRate] = useUpdateExchangeRateMutation();
  const [updateBalanceExpiration] = useUpdateBalanceExpirationMutation();
  const [updatePermissionLimit] = useUpdatePermissionLimitsMutation();

  const [settings, setSettings] = useState<StateValues>(initialState);

  const [isLoading, setIsLoading] =
    useState<LoadingStateValues>(initialLoadingState);

  const getSettings = async () => {
    try {
      setIsLoading(prev => ({
        ...prev,
        [Field.MinimumOrderValue]: true,
        [Field.MinimumTopUpValue]: true,
      }));

      const { minimumOrderValue, minimumTopUpValue } =
        await fetchSettings().unwrap();

      setSettings(prev => ({ ...prev, minimumOrderValue, minimumTopUpValue }));
    } catch (error) {
      errorHandler({ error, openNotification });
    } finally {
      setIsLoading(prev => ({
        ...prev,
        [Field.MinimumOrderValue]: false,
        [Field.MinimumTopUpValue]: false,
      }));
    }
  };

  const getAuthSettings = async () => {
    try {
      setIsLoading(prev => ({
        ...prev,
        [Field.AllowAccountActivation]: true,
        [Field.RequireInvitation]: true,
      }));

      const { allowAccountActivation, requireInvitation } =
        await fetchAuthSettings().unwrap();

      setSettings(prev => ({
        ...prev,
        allowAccountActivation,
        requireInvitation,
      }));
    } catch (error) {
      errorHandler({ error, openNotification });
    } finally {
      setIsLoading(prev => ({
        ...prev,
        [Field.AllowAccountActivation]: false,
        [Field.RequireInvitation]: false,
      }));
    }
  };

  const getBalanceExpiration = async () => {
    try {
      setIsLoading(prev => ({ ...prev, [Field.BalanceExpirationDays]: true }));

      const { days } = await fetchBalanceExpiration().unwrap();

      setSettings(prev => ({ ...prev, balanceExpirationDays: days }));
    } catch (error) {
      errorHandler({ error, openNotification });
    } finally {
      setIsLoading(prev => ({ ...prev, [Field.BalanceExpirationDays]: false }));
    }
  };

  const getExchangeRate = async () => {
    try {
      setIsLoading(prev => ({ ...prev, [Field.ExchangeRateValue]: true }));
      const { value } = await fetchExchangeRate().unwrap();

      setSettings(prev => ({ ...prev, exchangeRateValue: value }));
    } catch (error) {
      errorHandler({ error, openNotification });
    } finally {
      setIsLoading(prev => ({ ...prev, [Field.ExchangeRateValue]: false }));
    }
  };

  const getRolesLimits = async () => {
    try {
      setIsLoading(prev => ({
        ...prev,
        [Field.AgentRoleBalanceExpirationLimit]: true,
        [Field.AgentRoleRefundAddLimit]: true,
        [Field.ManagerRoleBalanceExpirationLimit]: true,
        [Field.ManagerRoleRefundAddLimit]: true,
      }));
      const data = await fetchPermissionsLimits().unwrap();

      const stateData = data.reduce((acc, { name, id, limit }) => {
        switch (name) {
          case Permission.RefundAddAgentLimitUpdate:
            acc[Field.AgentRoleRefundAddLimit] = limit;
            acc[Field.AgentRoleRefundAddLimitId] = id;
            break;
          case Permission.RefundAddManagerLimitUpdate:
            acc[Field.ManagerRoleRefundAddLimit] = limit;
            acc[Field.ManagerRoleRefundAddLimitId] = id;
            break;
          case Permission.BalanceExpirationAgentLimitUpdate:
            acc[Field.AgentRoleBalanceExpirationLimit] = limit;
            acc[Field.AgentRoleBalanceExpirationLimitId] = id;
            break;
          case Permission.BalanceExpirationManagerLimitUpdate:
            acc[Field.ManagerRoleBalanceExpirationLimit] = limit;
            acc[Field.ManagerRoleBalanceExpirationLimitId] = id;
            break;
        }

        return acc;
      }, {});

      setSettings(prev => ({ ...prev, ...stateData }));
    } catch (error) {
      errorHandler({ error, openNotification });
    } finally {
      setIsLoading(prev => ({
        ...prev,
        [Field.AgentRoleBalanceExpirationLimit]: false,
        [Field.AgentRoleRefundAddLimit]: false,
        [Field.ManagerRoleBalanceExpirationLimit]: false,
        [Field.ManagerRoleRefundAddLimit]: false,
      }));
    }
  };

  const handleUpdateAuthSettings = async (
    key: Field.AllowAccountActivation | Field.RequireInvitation,
    value: boolean,
  ) => {
    try {
      setIsLoading(prev => ({ ...prev, [key]: true }));

      const payload = {
        allowAccountActivation: settings.allowAccountActivation,
        requireInvitation: settings.requireInvitation,
        [key]: value,
      };

      setSettings(prev => ({ ...prev, [key]: value }));

      await updateAuthSettings(payload).unwrap();

      openNotification({ message: notificationMessage[key] });
    } catch (error) {
      setSettings(prev => ({ ...prev, [key]: !value }));

      errorHandler({ error, openNotification });
    } finally {
      setIsLoading(prev => ({ ...prev, [key]: false }));
    }
  };

  const handleUpdateSettings = async (
    key: Field.MinimumOrderValue | Field.MinimumTopUpValue,
    value: number,
  ) => {
    try {
      setIsLoading(prev => ({ ...prev, [key]: true }));

      const payload = {
        minimumOrderValue: settings.minimumOrderValue,
        minimumTopUpValue: settings.minimumTopUpValue,
        [key]: value,
      };

      await updateSettings(payload).unwrap();

      setSettings(prev => ({ ...prev, [key]: value }));

      openNotification({ message: notificationMessage[key] });
    } catch (error) {
      errorHandler({ error, openNotification });
    } finally {
      setIsLoading(prev => ({ ...prev, [key]: false }));
    }
  };

  const handleUpdateBalanceExpiration = async (
    key: Field.BalanceExpirationDays,
    days: number,
  ) => {
    try {
      setIsLoading(prev => ({ ...prev, [key]: true }));

      await updateBalanceExpiration({ days }).unwrap();

      setSettings(prev => ({ ...prev, [key]: days }));

      openNotification({ message: notificationMessage[key] });
    } catch (error) {
      errorHandler({ error, openNotification });
    } finally {
      setIsLoading(prev => ({ ...prev, [key]: false }));
    }
  };

  const handleUpdateExchangeRateValue = async (
    key: Field.ExchangeRateValue,
    value: number,
  ) => {
    try {
      setIsLoading(prev => ({ ...prev, [key]: true }));

      await updateExchangeRate({ value }).unwrap();

      setSettings(prev => ({ ...prev, [key]: value }));

      openNotification({ message: notificationMessage[key] });
    } catch (error) {
      errorHandler({ error, openNotification });
    } finally {
      setIsLoading(prev => ({ ...prev, [key]: false }));
    }
  };

  const handleUpdateLimitValue = async (
    key:
      | Field.AgentRoleBalanceExpirationLimit
      | Field.AgentRoleRefundAddLimit
      | Field.ManagerRoleBalanceExpirationLimit
      | Field.ManagerRoleRefundAddLimit,
    value: number,
  ) => {
    try {
      setIsLoading(prev => ({ ...prev, [key]: true }));

      await updatePermissionLimit({
        id: settings[`${key}Id`],
        limit: value,
      }).unwrap();

      setSettings(prev => ({ ...prev, [key]: value }));

      openNotification({ message: notificationMessage[key] });
    } catch (error) {
      errorHandler({ error, openNotification });
    } finally {
      setIsLoading(prev => ({ ...prev, [key]: false }));
    }
  };

  const handleUpdate = (key: Field, value: StateValues[Field]) => {
    switch (key) {
      case Field.AllowAccountActivation:
      case Field.RequireInvitation:
        handleUpdateAuthSettings(key, value as boolean);
        return;

      case Field.MinimumOrderValue:
      case Field.MinimumTopUpValue:
        handleUpdateSettings(key, value as number);
        return;

      case Field.BalanceExpirationDays:
        handleUpdateBalanceExpiration(key, value as number);
        return;

      case Field.ExchangeRateValue:
        handleUpdateExchangeRateValue(key, value as number);
        return;

      case Field.AgentRoleBalanceExpirationLimit:
      case Field.AgentRoleRefundAddLimit:
      case Field.ManagerRoleBalanceExpirationLimit:
      case Field.ManagerRoleRefundAddLimit:
        handleUpdateLimitValue(key, value as number);
        return;

      default:
        break;
    }
  };

  useEffect(() => {
    if (!canSettingsGet) {
      return;
    }

    getSettings();
    getAuthSettings();
    getBalanceExpiration();
    getExchangeRate();
    getRolesLimits();
  }, []);

  return (
    <FlexContainer $fullwidth $column $gap={24}>
      <PageHeadingContainer>
        <PageTitleContainer>
          <Typography.H1>Settings</Typography.H1>
        </PageTitleContainer>
      </PageHeadingContainer>

      <FlexContainer $fullwidth $column $gap={16}>
        <ContentBox $column $gap={12}>
          <Typography.H2>Currency</Typography.H2>

          <div>
            <SettingsItem
              title="Exchange rate"
              description="Set FM coin exchange rate"
            >
              <Input
                step={0.1}
                isLoading={isLoading.exchangeRateValue}
                canUpdate={canSettingsUpdate && canExchangeRateUpdate}
                data={settings.exchangeRateValue}
                onSubmit={value => handleUpdate(Field.ExchangeRateValue, value)}
              />
            </SettingsItem>

            <SettingsItem
              title="Virtual currency expiration time"
              description="Set the expiration date when the user topup the balance"
            >
              <Input
                isLoading={isLoading.balanceExpirationDays}
                canUpdate={canSettingsUpdate && canBalanceExpirationUpdate}
                data={settings.balanceExpirationDays}
                onSubmit={value =>
                  handleUpdate(Field.BalanceExpirationDays, value)
                }
              />
            </SettingsItem>
          </div>
        </ContentBox>

        <ContentBox $column $gap={12}>
          <Typography.H2>Allow friends invites in the app</Typography.H2>

          <div>
            <SettingsItem title="Enable invite friends code sharing">
              <Switch
                isLoading={isLoading.allowAccountActivation}
                canUpdate={canSettingsUpdate}
                data={settings.allowAccountActivation}
                onChange={value =>
                  handleUpdate(Field.AllowAccountActivation, value)
                }
              />
            </SettingsItem>

            <SettingsItem title="Require invitation to access app">
              <Switch
                isLoading={isLoading.requireInvitation}
                canUpdate={canSettingsUpdate}
                data={settings.requireInvitation}
                onChange={value => handleUpdate(Field.RequireInvitation, value)}
              />
            </SettingsItem>
          </div>
        </ContentBox>

        <ContentBox $column $gap={12}>
          <Typography.H2>Minimum values</Typography.H2>

          <div>
            <SettingsItem
              title="Order (FM coins)"
              description="Set the minimum coins required to proceed with the order"
            >
              <Input
                isLoading={isLoading.minimumOrderValue}
                canUpdate={canSettingsUpdate}
                data={settings.minimumOrderValue}
                onSubmit={value => handleUpdate(Field.MinimumOrderValue, value)}
              />
            </SettingsItem>

            <SettingsItem
              title="Top up (KWD)"
              description="Set the minimum to top up the balance"
            >
              <Input
                suffix="KWD"
                isLoading={isLoading.minimumTopUpValue}
                canUpdate={canSettingsUpdate}
                data={settings.minimumTopUpValue}
                onSubmit={value => handleUpdate(Field.MinimumTopUpValue, value)}
              />
            </SettingsItem>
          </div>
        </ContentBox>

        <ContentBox $column $gap={12}>
          <Typography.H2>Role Settings</Typography.H2>

          <div>
            <SettingsItem
              title="Agent role refund (add) limit"
              description="Set refund limit for agent role"
            >
              <Input
                isLoading={isLoading.agentRoleRefundAddLimit}
                canUpdate={canSettingsUpdate}
                data={settings.agentRoleRefundAddLimit}
                onSubmit={value =>
                  handleUpdate(Field.AgentRoleRefundAddLimit, value)
                }
              />
            </SettingsItem>

            <SettingsItem
              title="Agent role balance expiration limit"
              description="Set balance expiration days for agent role"
            >
              <Input
                suffix="days"
                isLoading={isLoading.agentRoleBalanceExpirationLimit}
                canUpdate={canSettingsUpdate}
                data={settings.agentRoleBalanceExpirationLimit}
                onSubmit={value =>
                  handleUpdate(Field.AgentRoleBalanceExpirationLimit, value)
                }
              />
            </SettingsItem>

            <SettingsItem
              title="Manager role refund (add) limit"
              description="Set refund limit for manager role"
            >
              <Input
                isLoading={isLoading.managerRoleRefundAddLimit}
                canUpdate={canSettingsUpdate}
                data={settings.managerRoleRefundAddLimit}
                onSubmit={value =>
                  handleUpdate(Field.ManagerRoleRefundAddLimit, value)
                }
              />
            </SettingsItem>

            <SettingsItem
              title="Manager role balance expiration limit"
              description="Set balance expiration days for manager role"
            >
              <Input
                suffix="days"
                isLoading={isLoading.managerRoleBalanceExpirationLimit}
                canUpdate={canSettingsUpdate}
                data={settings.managerRoleBalanceExpirationLimit}
                onSubmit={value =>
                  handleUpdate(Field.ManagerRoleBalanceExpirationLimit, value)
                }
              />
            </SettingsItem>
          </div>
        </ContentBox>
      </FlexContainer>
    </FlexContainer>
  );
};
